import { type CSSProperties } from 'react'
import { Text } from '@stone-payments/jade'
import { cn } from 'investments/common/utils'

const ROUND_PRECISION = 1000

function getPercentage({ min, max, now }: { min: number; max: number; now: number }) {
  const percentage = ((now - min) / (max - min)) * 100
  return Math.round(percentage * ROUND_PRECISION) / ROUND_PRECISION
}

/**
 * A progress bar is a visual representation of the progress of a task.
 *
 * @param max - The maximum value of the progress bar.
 * @param min - The minimum value of the progress bar.
 * @param now - The current value of the progress bar.
 * @param supportText - The text that will be displayed below the progress bar.
 * @param variant - The color variant of the progress bar.
 */
export function ProgressBar({
  max = 100,
  min = 0,
  now,
  supportText,
  showPercentage = true,
  variant = 'brand',
  size = 'default'
}: {
  min?: number
  max?: number
  now: number
  supportText?: string
  showPercentage?: boolean
  size?: 'small' | 'default'
  variant?: 'negative' | 'positive' | 'neutral' | 'warning' | 'brand' | 'info'
}) {
  const progress = getPercentage({ min, max, now })

  return (
    <div className="flex flex-col items-end gap-2 w-full">
      <div
        className="flex items-center gap-2 size-full rounded-jade-xsmall overflow-hidden bg-jade-background-body"
        id="progressbar"
        title={`${progress}% de progresso`}
      >
        <span
          role="progressbar"
          data-size={size}
          data-variant={variant}
          aria-valuenow={now}
          aria-valuemin={min}
          aria-valuemax={max}
          style={
            {
              '--progress': `${progress}%`
            } as CSSProperties
          }
          className={cn(
            'flex items-center justify-end rounded-jade-xsmall data-[size=default]:h-7 data-[size=small]:h-[0.44rem]',
            'transition-all duration-300 delay-300 ease-in-out w-[var(--progress)]',
            'data-[variant=negative]:bg-jade-background-negative data-[variant=positive]:bg-jade-background-positive data-[variant=neutral]:bg-jade-background-neutral data-[variant=warning]:bg-jade-background-warning data-[variant=brand]:bg-jade-background-brand data-[variant=info]:bg-jade-background-info'
          )}
        >
          {showPercentage && progress >= 15 ? (
            <Text color="on-brand-medium" weight="semibold" variant="text-small" className="py-0 !px-2">
              {progress}%
            </Text>
          ) : null}
        </span>
        {showPercentage && progress < 15 ? (
          <Text color="medium" weight="semibold" variant="text-small" className="py-0 px-2">
            {progress}%
          </Text>
        ) : null}
      </div>
      {supportText ? (
        <Text variant="text-small" weight="medium" color="medium">
          {supportText}
        </Text>
      ) : null}
    </div>
  )
}
